import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, debounceTime, distinctUntilChanged, interval, takeUntil } from 'rxjs';
import { ChatService } from '../services/chat.service';
import { UserSelectionListItem } from '../shared/user-selection-list/user-selection-list.component';
import {
  ChatMessage,
  ConversationSummary,
  CreateConversationRequest,
  NotificationRecord,
} from './chat.types';

@Component({
  selector: 'app-chat-service',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css'],
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageContainer') messageContainer!: ElementRef;
  conversations: ConversationSummary[] = [];
  allConversations: ConversationSummary[] = [];
  hiddenConversations: ConversationSummary[] = [];
  selectedConversation: ConversationSummary | null = null;
  messages: ChatMessage[] = [];

  conversationCursor: string | null = null;
  messagesCursor: string | null = null;

  conversationsLoading = false;
  messagesLoading = false;
  isSending = false;
  creatingConversation = false;

  searchControl = new FormControl<string>('');
  messageForm: FormGroup;
  createConversationForm: FormGroup;
  renameConversationForm: FormGroup;

  currentUserId = '';
  currentUserLabel = '';
  currentUser: any = null;
  createModalVisible = false;
  private currentSearchTerm = '';
  private hiddenConversationIds = new Set<string>();
  private hiddenReadSyncInFlight = new Set<string>();
  hiddenDrawerOpen = false;

  availableUsers: ParticipantOption[] = [];
  filteredUsers: ParticipantOption[] = [];
  usersLoading = false;
  userSearchControl = new FormControl<string>('');
  selectedCompanyId: string | null = null;
  currentCompanyLabel = '';
  selectedParticipantIds = new Set<string>();
  selectedUsers: ParticipantOption[] = [];
  userSelectionItems: UserSelectionListItem[] = [];
  readonly participantSelectionChecker = (userId: string) =>
    this.isParticipantSelected(userId);
  private participantDirectory = new Map<string, string>();
  private userDirectoryCache = new Map<string, ParticipantOption[]>();
  notificationPreview: NotificationRecord[] = [];
  notificationPreviewLoading = false;
  notificationsModalVisible = false;
  notificationsList: NotificationRecord[] = [];
  notificationsCursor: string | null = null;
  notificationsLoading = false;
  renameModalVisible = false;
  renamingConversation = false;
  manageModalVisible = false;
  managingParticipants = false;
  manageUserSearchControl = new FormControl<string>('');
  manageFilteredUsers: ParticipantOption[] = [];
  manageUserSelectionItems: UserSelectionListItem[] = [];
  readonly manageSelectionChecker = (_userId: string) => false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly chatService: ChatService,
    private readonly fb: FormBuilder,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.messageForm = this.fb.group({
      body: ['', [Validators.required]],
    });

    this.createConversationForm = this.fb.group({
      title: [''],
    });

    this.renameConversationForm = this.fb.group({
      title: [''],
    });
  }

  ngOnInit(): void {
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      try {
        const parsed = JSON.parse(rawUser);
        this.currentUser = parsed;
        console.log('currentUser: ', this.currentUser)
        this.currentUserId = this.resolveUserIdentifier(parsed);
        this.selectedCompanyId = parsed?.company_id ?? null;
        this.currentCompanyLabel = this.resolveCompanyLabelFromUser(parsed);
        const firstName = parsed?.first_name ?? parsed?.firstName ?? '';
        const lastName = parsed?.last_name ?? parsed?.lastName ?? '';
        this.currentUserLabel =
          [firstName, lastName].filter(Boolean).join(' ') ||
          parsed?.email ||
          '';
        if (this.currentUserId) {
          this.registerParticipantLabel(
            this.currentUserId,
            this.currentUserLabel || this.currentUserId,
          );
        }
      } catch (error) {
        this.message.error('Unable to read user context.');
      }
    }

    if (!this.currentUserId) {
      this.message.error('No authenticated user found.');
      return;
    }

    this.loadHiddenConversations();

    if (!this.selectedCompanyId) {
      this.selectedCompanyId = this.currentUser?.company_id ?? null;
    }

    this.loadAvailableUsers();
    this.loadConversations(true);
    this.loadNotificationPreview();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.currentSearchTerm = (value ?? '').trim();
        this.loadConversations(true);
      });

    this.userSearchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.filterUsers(term);
      });

    this.manageUserSearchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.filterManageUsers(term);
      });

    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshConversationsSilently();
        this.refreshSelectedConversationMessages();
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadConversations(reset = false): void {
    if (!this.currentUserId || (this.conversationsLoading && !reset)) {
      return;
    }

    if (reset) {
      this.conversations = [];
      this.allConversations = [];
      this.hiddenConversations = [];
      this.conversationCursor = null;
    } else if (!this.conversationCursor) {
      return;
    }

    this.conversationsLoading = true;
    this.chatService
      .listConversations(this.currentUserId, {
        limit: 20,
        cursor: reset ? null : this.conversationCursor,
        search: this.currentSearchTerm || null,
      })
      .subscribe({
        next: (response) => {
          const merged = reset
            ? response.items
            : [...this.allConversations, ...response.items];
          this.allConversations = this.sortConversations(merged);
          this.applyConversationVisibility();

          this.conversationCursor = response.nextCursor;

          if (
            reset &&
            this.selectedConversation &&
            !this.allConversations.some(
              (item) => item.id === this.selectedConversation?.id
            )
          ) {
            this.selectedConversation = null;
            this.messages = [];
            this.messagesCursor = null;
          }
          if (this.selectedConversation) {
            const updatedSelection = this.allConversations.find(
              (item) => item.id === this.selectedConversation?.id,
            );
            if (updatedSelection) {
              this.selectedConversation = updatedSelection;
            }
          }

          this.conversationsLoading = false;
        },
        error: () => {
          this.conversationsLoading = false;
          this.message.error('Unable to load conversations.');
        },
      });
  }

  selectConversation(conversation: ConversationSummary): void {
    if (this.selectedConversation?.id === conversation.id) {
      return;
    }

    this.selectedConversation = conversation;
    this.messages = [];
    this.messagesCursor = null;
    this.loadMessages(true);
    this.refreshManageUsers();
    this.markConversationRead(conversation);
  }

  loadMessages(reset = false): void {
    if (!this.selectedConversation || !this.currentUserId) {
      return;
    }

    if (reset) {
      this.messages = [];
      this.messagesCursor = null;
    } else if (!this.messagesCursor || this.messagesLoading) {
      return;
    }

    this.messagesLoading = true;
    this.chatService
      .listMessages(this.selectedConversation.id, {
        userId: this.currentUserId,
        limit: 30,
        cursor: reset ? null : this.messagesCursor,
      })
      .subscribe({
        next: (response) => {
          this.messages = reset
            ? response.items
            : [...response.items, ...this.messages];

          this.messagesCursor = response.nextCursor;
          this.messagesLoading = false;
          if (reset && this.selectedConversation) {
            const refreshedConversation = this.allConversations.find(
              (item) => item.id === this.selectedConversation?.id,
            );
            if (refreshedConversation) {
              this.markConversationRead(refreshedConversation);
            }
          }
          if (reset) {
            this.scrollToBottom(0);
          }
        },
        error: () => {
          this.messagesLoading = false;
          this.message.error('Unable to load messages.');
        },
      });
  }

  loadNotificationPreview(): void {
    if (!this.currentUserId || this.notificationPreviewLoading) {
      return;
    }

    this.notificationPreviewLoading = true;
    this.chatService
      .listNotifications(this.currentUserId, { limit: 3 })
      .subscribe({
        next: (response) => {
          this.notificationPreview = response.items;
          this.notificationPreviewLoading = false;
        },
        error: () => {
          this.notificationPreviewLoading = false;
          this.message.error('Unable to load notifications.');
        },
      });
  }

  openNotificationsModal(): void {
    if (!this.currentUserId) {
      return;
    }
    this.notificationsModalVisible = true;
    if (!this.notificationsList.length && this.notificationPreview.length) {
      this.notificationsList = [...this.notificationPreview];
    }
    this.loadUserNotifications(true);
  }

  closeNotificationsModal(): void {
    if (!this.notificationsLoading) {
      this.notificationsModalVisible = false;
    }
  }

  loadUserNotifications(reset = false): void {
    if (!this.currentUserId || (this.notificationsLoading && !reset)) {
      return;
    }

    if (!reset && !this.notificationsCursor) {
      return;
    }

    const cursor = reset ? null : this.notificationsCursor;
    this.notificationsLoading = true;
    this.chatService
      .listNotifications(this.currentUserId, {
        limit: 20,
        cursor,
      })
      .subscribe({
        next: (response) => {
          this.notificationsList = reset
            ? response.items
            : [...this.notificationsList, ...response.items];
          this.notificationsCursor = response.nextCursor;
          this.notificationsLoading = false;
          if (reset) {
            this.notificationPreview = response.items.slice(0, 3);
          }
        },
        error: () => {
          this.notificationsLoading = false;
          this.message.error('Unable to load notifications.');
        },
      });
  }

  getNotificationTitle(notification: NotificationRecord): string {
    return (
      notification.title?.trim() ||
      notification.type?.toString() ||
      'Notification'
    );
  }

  trackNotificationById(_: number, notification: NotificationRecord): string {
    return notification.id;
  }

  trackConversationById(_: number, conversation: ConversationSummary): string {
    return conversation.id;
  }

  sendMessage(): void {
    if (
      !this.selectedConversation ||
      !this.currentUserId ||
      this.messageForm.invalid ||
      this.isSending
    ) {
      return;
    }

    const rawBody = (this.messageForm.value.body ?? '').trim();
    if (!rawBody) {
      this.messageForm.reset();
      return;
    }

    this.isSending = true;
    this.chatService
      .sendMessage(this.selectedConversation.id, {
        userId: this.currentUserId,
        body: rawBody,
      })
      .subscribe({
        next: (message) => {
          this.messages = [...this.messages, message];
          this.messageForm.reset();
          this.isSending = false;
          this.scrollToBottom();
          if (this.selectedConversation) {
            const updatedConversation = {
              ...this.selectedConversation,
              lastMessage: message,
              updated_at: message.created_at,
              lastReadAt: message.created_at,
              hasUnread: false,
            };
            this.selectedConversation = updatedConversation;
            this.allConversations = this.sortConversations(
              this.allConversations.map((item) =>
                item.id === updatedConversation.id ? updatedConversation : item,
              ),
            );
            this.applyConversationVisibility();
          }
          this.sendPushForConversation(rawBody);
        },
        error: () => {
          this.isSending = false;
          this.message.error('Failed to send message.');
        },
      });
  }

  private sendPushForConversation(messageBody: string): void {
    if (!this.selectedConversation || !this.currentUserId) {
      return;
    }

    const recipientIds = (this.selectedConversation.participantIds || [])
      .map((id) => String(id))
      .filter((id) => id && id !== this.currentUserId);

    if (!recipientIds.length) {
      return;
    }

    const senderName = this.currentUserLabel || 'Someone';
    this.chatService
      .sendChatPush({
        user_ids: recipientIds,
        title: senderName,
        message: messageBody,
        data: {
          type: 'chat_message',
          conversation_id: this.selectedConversation.id,
        },
      })
      .subscribe({
        next: (response) => {
          if (response?.result === 'success' && Number(response?.sent || 0) > 0) {
            return;
          }
          console.warn('Push trigger response:', response);
        },
        error: () => {
          console.error('Unable to trigger push notification.');
        },
      });
  }

  openCreateModal(): void {
    this.createConversationForm.reset();
    this.resetParticipantSelections();
    this.createModalVisible = true;
    this.loadAvailableUsers(true);
  }

  closeCreateModal(): void {
    if (!this.creatingConversation) {
      this.createModalVisible = false;
    }
  }

  createConversation(): void {
    if (!this.currentUserId || this.creatingConversation) {
      return;
    }

    if (!this.selectedCompanyId) {
      this.message.warning('Select a company before starting a conversation.');
      return;
    }

    if (this.createConversationForm.invalid) {
      this.createConversationForm.markAllAsTouched();
      return;
    }

    const participantIds = Array.from(this.selectedParticipantIds).filter(
      (value) => value && value !== this.currentUserId
    );

    if (!participantIds.length) {
      this.message.warning('Select at least one participant.');
      return;
    }
    const rawTitle = (this.createConversationForm.value.title ?? '').toString();
    const trimmedTitle = rawTitle.trim();

    const payload: CreateConversationRequest = {
      userId: this.currentUserId,
      participantIds,
      title: trimmedTitle || this.buildDefaultConversationTitle(participantIds),
    };

    this.creatingConversation = true;
    this.chatService.createConversation(payload).subscribe({
      next: (conversation) => {
        this.creatingConversation = false;
        this.createModalVisible = false;

        const existingIndex = this.allConversations.findIndex(
          (item) => item.id === conversation.id,
        );
        if (existingIndex > -1) {
          const updated = [...this.allConversations];
          updated.splice(existingIndex, 1);
          this.allConversations = this.sortConversations([conversation, ...updated]);
          this.applyConversationVisibility();
          this.message.info('Conversation already exists. Opening it now.');
        } else {
          this.allConversations = this.sortConversations([conversation, ...this.allConversations]);
          this.applyConversationVisibility();
          this.message.success('Conversation created.');
        }

        this.selectConversation(conversation);
      },
      error: () => {
        this.creatingConversation = false;
        this.message.error('Failed to create conversation.');
      },
    });
  }

  openRenameModal(): void {
    if (!this.selectedConversation) {
      this.message.warning('Select a conversation first.');
      return;
    }
    this.renameConversationForm.setValue({
      title: this.selectedConversation.title || '',
    });
    this.renameModalVisible = true;
  }

  closeRenameModal(): void {
    if (!this.renamingConversation) {
      this.renameModalVisible = false;
    }
  }

  renameConversation(): void {
    if (!this.selectedConversation || !this.currentUserId || this.renamingConversation) {
      return;
    }

    const rawTitle = (this.renameConversationForm.value.title ?? '').toString();
    const trimmedTitle = rawTitle.trim();

    this.renamingConversation = true;
    this.chatService
      .renameConversation(this.selectedConversation.id, {
        userId: this.currentUserId,
        title: trimmedTitle || null,
      })
      .subscribe({
        next: (conversation) => {
          this.renamingConversation = false;
          this.renameModalVisible = false;
          this.message.success('Conversation updated.');
          this.selectedConversation = conversation;
          const index = this.allConversations.findIndex(
            (item) => item.id === conversation.id,
          );
          if (index > -1) {
            const updated = [...this.allConversations];
            updated.splice(index, 1);
            this.allConversations = this.sortConversations([conversation, ...updated]);
            this.applyConversationVisibility();
          } else {
            this.allConversations = this.sortConversations([conversation, ...this.allConversations]);
            this.applyConversationVisibility();
          }
        },
        error: () => {
          this.renamingConversation = false;
          this.message.error('Failed to update conversation.');
        },
      });
  }

  canManageParticipants(): boolean {
    return (
      !!this.selectedConversation &&
      !!this.currentUserId &&
      this.selectedConversation.created_by === this.currentUserId
    );
  }

  openManageParticipantsModal(): void {
    if (!this.selectedConversation) {
      this.message.warning('Select a conversation first.');
      return;
    }
    if (!this.canManageParticipants()) {
      this.message.warning('Only the conversation creator can manage participants.');
      return;
    }
    this.manageUserSearchControl.setValue('', { emitEvent: false });
    this.manageModalVisible = true;
    this.loadAvailableUsers(true);
    this.refreshManageUsers();
  }

  closeManageParticipantsModal(): void {
    if (!this.managingParticipants) {
      this.manageModalVisible = false;
    }
  }

  addParticipantToConversation(participantId: string): void {
    if (
      !this.selectedConversation ||
      !this.currentUserId ||
      !this.canManageParticipants() ||
      this.managingParticipants
    ) {
      return;
    }

    this.managingParticipants = true;
    this.chatService
      .addParticipant(this.selectedConversation.id, {
        userId: this.currentUserId,
        participantId,
      })
      .subscribe({
        next: (conversation) => {
          this.managingParticipants = false;
          this.message.success('Participant added.');
          this.selectedConversation = conversation;
          const index = this.allConversations.findIndex(
            (item) => item.id === conversation.id,
          );
          if (index > -1) {
            const updated = [...this.conversations];
            updated.splice(index, 1);
            this.conversations = this.sortConversations([conversation, ...updated]);
          } else {
            this.conversations = this.sortConversations([conversation, ...this.conversations]);
          }
          this.refreshManageUsers();
        },
        error: () => {
          this.managingParticipants = false;
          this.message.error('Failed to add participant.');
        },
      });
  }

  handleManageToggle(event: { userId: string; checked: boolean }): void {
    if (!event.checked) {
      return;
    }
    this.addParticipantToConversation(event.userId);
  }

  removeParticipantFromConversation(participantId: string): void {
    if (
      !this.selectedConversation ||
      !this.currentUserId ||
      !this.canManageParticipants() ||
      this.managingParticipants
    ) {
      return;
    }

    if (participantId === this.currentUserId) {
      this.message.warning('You must remain in the conversation.');
      return;
    }

    this.managingParticipants = true;
    this.chatService
      .removeParticipant(this.selectedConversation.id, participantId, this.currentUserId)
      .subscribe({
        next: (response) => {
          this.managingParticipants = false;
          if (response.disbanded) {
            this.message.info('Conversation disbanded.');
            this.allConversations = this.allConversations.filter(
              (item) => item.id !== response.conversationId,
            );
            this.applyConversationVisibility();
            if (this.selectedConversation?.id === response.conversationId) {
              this.selectedConversation = null;
              this.messages = [];
              this.messagesCursor = null;
            }
            this.manageModalVisible = false;
            return;
          }

          if (response.conversation) {
            const updatedConversation = response.conversation;
            this.message.success('Participant removed.');
            this.selectedConversation = updatedConversation;
            const index = this.allConversations.findIndex(
              (item) => item.id === updatedConversation.id,
            );
            if (index > -1) {
              const updated = [...this.allConversations];
              updated.splice(index, 1);
              this.allConversations = this.sortConversations([updatedConversation, ...updated]);
              this.applyConversationVisibility();
            } else {
              this.allConversations = this.sortConversations([
                updatedConversation,
                ...this.allConversations,
              ]);
              this.applyConversationVisibility();
            }
          }
          this.refreshManageUsers();
        },
        error: () => {
          this.managingParticipants = false;
          this.message.error('Failed to remove participant.');
        },
      });
  }

  scrollToBottom(duration = 300): void {
    setTimeout(() => {
      if (this.messageContainer && this.messageContainer.nativeElement) {
        const element = this.messageContainer.nativeElement;
        element.scrollTo({
          top: element.scrollHeight,
          behavior: duration === 0 ? 'auto' : 'smooth'
        });
      }
    }, 100);
  }

  private markConversationRead(conversation: ConversationSummary): void {
    if (!this.currentUserId || !conversation.hasUnread) {
      return;
    }
    const lastReadAt =
      conversation.lastMessage?.created_at ??
      conversation.updated_at ??
      new Date().toISOString();

    this.patchConversation(conversation.id, {
      hasUnread: false,
      lastReadAt,
    });

    this.chatService
      .markConversationRead(conversation.id, {
        userId: this.currentUserId,
        lastReadAt,
      })
      .subscribe({
        next: (response) => {
          this.patchConversation(conversation.id, {
            hasUnread: false,
            lastReadAt: response.lastReadAt,
          });
          this.chatService.requestUnreadRefresh();
        },
        error: () => {
          this.message.error('Failed to mark conversation as read.');
        },
      });
  }

  private patchConversation(
    conversationId: string,
    changes: Partial<ConversationSummary>,
  ): void {
    this.allConversations = this.sortConversations(
      this.allConversations.map((item) =>
        item.id === conversationId ? { ...item, ...changes } : item,
      ),
    );
    this.applyConversationVisibility();
    if (this.selectedConversation?.id === conversationId) {
      this.selectedConversation = {
        ...this.selectedConversation,
        ...changes,
      };
    }
  }

  private sortConversations(
    conversations: ConversationSummary[],
  ): ConversationSummary[] {
    return [...conversations].sort((a, b) => {
      const unreadDelta = Number(!!b.hasUnread) - Number(!!a.hasUnread);
      if (unreadDelta !== 0) {
        return unreadDelta;
      }
      const activityDelta =
        this.getConversationActivityTime(b) -
        this.getConversationActivityTime(a);
      if (activityDelta !== 0) {
        return activityDelta;
      }
      return a.id.localeCompare(b.id);
    });
  }

  private getConversationActivityTime(
    conversation: ConversationSummary,
  ): number {
    const candidate =
      conversation.lastMessage?.created_at ??
      conversation.updated_at ??
      conversation.created_at ??
      null;
    if (!candidate) {
      return 0;
    }
    const time = Date.parse(candidate);
    return Number.isNaN(time) ? 0 : time;
  }

  getSenderLabel(senderId: string): string {
    if (senderId === this.currentUserId) {
      return 'You';
    }
    return this.getParticipantLabel(senderId) || senderId;
  }

  getParticipantSummary(conversation: ConversationSummary | null): string {
    if (!conversation) {
      return '';
    }
    const participants = conversation.participantIds
      .map((id) => this.getParticipantLabel(id) || id)
      .filter((label) => !!label?.trim());
    if (!participants.length) {
      return 'Participants unavailable';
    }
    return participants.join(', ');
  }

  getConversationTitle(conversation: ConversationSummary): string {
    if (conversation.title) {
      return conversation.title;
    }
    const participantIds = conversation.participantIds ?? [];
    if (participantIds.length === 2) {
      const otherParticipantId =
        participantIds.find((id) => id !== this.currentUserId) ??
        participantIds[0];
      const otherLabel =
        this.getParticipantLabel(otherParticipantId) || otherParticipantId;
      if (otherLabel?.trim()) {
        return otherLabel;
      }
    }
    const participantCount = participantIds.length;
    return `Conversation (${participantCount} participants)`;
  }

  private buildDefaultConversationTitle(participantIds: string[]): string {
    if (participantIds.length === 1) {
      const participantId = participantIds[0];
      return this.getParticipantLabel(participantId) || `User ${participantId}`;
    }
    return 'Group chat';
  }

  getRemovableParticipantIds(): string[] {
    if (!this.selectedConversation) {
      return [];
    }
    return (this.selectedConversation.participantIds ?? []).filter(
      (participantId) => participantId !== this.selectedConversation?.created_by,
    );
  }

  toggleParticipant(userId: string, forceState?: boolean): void {
    if (!userId) {
      return;
    }

    const normalizedId = String(userId);
    const shouldSelect =
      forceState !== undefined
        ? forceState
        : !this.selectedParticipantIds.has(normalizedId);

    if (shouldSelect) {
      this.selectedParticipantIds.add(normalizedId);
    } else {
      this.selectedParticipantIds.delete(normalizedId);
    }
    this.syncSelectedUsers();
  }

  isParticipantSelected(userId: string): boolean {
    if (!userId) {
      return false;
    }
    return this.selectedParticipantIds.has(String(userId));
  }

  private loadAvailableUsers(forceReload = false): void {
    if (this.usersLoading) {
      return;
    }

    const companyId = this.selectedCompanyId;
    if (!companyId) {
      this.availableUsers = [];
      this.filteredUsers = [];
      return;
    }

    if (!forceReload && this.userDirectoryCache.has(companyId)) {
      this.availableUsers = this.userDirectoryCache.get(companyId) ?? [];
      this.indexParticipants(this.availableUsers);
      this.filterUsers(this.userSearchControl.value);
      this.syncSelectedUsers();
      return;
    }

    if (forceReload) {
      this.userDirectoryCache.delete(companyId);
    }

    this.usersLoading = true;
    this.chatService.listCompanyUsers(companyId).subscribe({
      next: (response) => {
        const rows = this.extractUserArray(response);
        const normalizedUsers: ParticipantOption[] =
          rows
            .map((user: any) => {
              const participantId = user?.id ?? user?.user_id ?? user?.userId;
              const firstName = user?.first_name ?? user?.firstName ?? '';
              const lastName = user?.last_name ?? user?.lastName ?? '';
              const email = user?.email ?? '';
              const displayName =
                this.composeDisplayName(
                  { first_name: firstName, last_name: lastName, email },
                  participantId ? `User ${participantId}` : '',
                ) || participantId || '';

              return {
                id: participantId ? String(participantId) : '',
                first_name: firstName,
                last_name: lastName,
                email,
                displayName,
                searchIndex: `${firstName} ${lastName} ${email}`.toLowerCase(),
              };
            })
            .filter(
              (user: ParticipantOption) =>
                user.id && user.id !== String(this.currentUserId),
            ) ?? [];

        this.availableUsers = normalizedUsers;
        this.userDirectoryCache.set(companyId, normalizedUsers);
        const companyLabel =
          this.extractCompanyLabelFromUsers(rows) ||
          this.currentCompanyLabel ||
          this.resolveCompanyLabelFromUser(this.currentUser);
        if (companyLabel) {
          this.currentCompanyLabel = companyLabel;
        }
        this.indexParticipants(normalizedUsers);
        this.usersLoading = false;
        this.filterUsers(this.userSearchControl.value);
        this.refreshManageUsers();
        this.syncSelectedUsers();
      },
      error: () => {
        this.usersLoading = false;
        this.userDirectoryCache.delete(companyId);
        this.message.error('Unable to load users for the selected company.');
      },
    });

  }

  private resetParticipantSelections(): void {
    this.selectedParticipantIds.clear();
    this.selectedUsers = [];
    this.userSearchControl.setValue('', { emitEvent: false });
    this.filteredUsers = [...this.availableUsers];
  }

  private filterUsers(rawTerm?: string | null): void {
    const term = (rawTerm ?? '').trim().toLowerCase();
    this.filteredUsers =
      term.length === 0
        ? [...this.availableUsers]
        : this.availableUsers.filter((user) => user.searchIndex.includes(term));

    this.userSelectionItems = this.filteredUsers.map((user) =>
      this.mapParticipantToListItem(user),
    );
    this.cdr.markForCheck();
  }

  private filterManageUsers(rawTerm?: string | null): void {
    const term = (rawTerm ?? '').trim().toLowerCase();
    const addableUsers = this.getAddableUsers();
    this.manageFilteredUsers =
      term.length === 0
        ? [...addableUsers]
        : addableUsers.filter((user) => user.searchIndex.includes(term));
    this.manageUserSelectionItems = this.manageFilteredUsers.map((user) =>
      this.mapParticipantToListItem(user),
    );
    this.cdr.markForCheck();
  }

  private refreshManageUsers(): void {
    if (!this.manageModalVisible) {
      return;
    }
    this.filterManageUsers(this.manageUserSearchControl.value);
  }

  private getAddableUsers(): ParticipantOption[] {
    if (!this.selectedConversation) {
      return [];
    }
    const participantSet = new Set(
      (this.selectedConversation.participantIds ?? []).map((id) => String(id)),
    );
    return this.availableUsers.filter((user) => !participantSet.has(user.id));
  }

  private syncSelectedUsers(): void {
    this.selectedUsers = this.availableUsers.filter((user) =>
      this.selectedParticipantIds.has(user.id),
    );
  }

  private mapParticipantToListItem(user: ParticipantOption): UserSelectionListItem {
    const primary =
      user.displayName ||
      this.composeDisplayName(
        {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
        user.id,
      ) ||
      user.id;

    const secondary = user.email?.trim() || undefined;

    return {
      id: user.id,
      primaryText: primary,
      secondaryText: secondary,
    };
  }

  private indexParticipants(users: ParticipantOption[]): void {
    users.forEach((user) => {
      if (!user.id) {
        return;
      }
      const label =
        user.displayName ||
        this.composeDisplayName(
          {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
          },
          user.id,
        );
      this.registerParticipantLabel(user.id, label);
    });
  }

  private getParticipantLabel(userId: string): string {
    if (!userId) {
      return '';
    }
    const normalizedId = String(userId);
    if (normalizedId === String(this.currentUserId)) {
      return this.currentUserLabel || this.participantDirectory.get(normalizedId) || '';
    }
    return this.participantDirectory.get(normalizedId) || '';
  }

  getParticipantDisplayName(userId: string): string {
    return this.getParticipantLabel(userId) || userId;
  }

  private registerParticipantLabel(userId: string, label?: string): void {
    if (!userId) {
      return;
    }
    const normalizedId = String(userId);
    const normalizedLabel = label?.trim();
    this.participantDirectory.set(normalizedId, normalizedLabel || normalizedId);
  }

  private composeDisplayName(
    user: Partial<ParticipantOption> & { firstName?: string; lastName?: string; email?: string },
    fallback = '',
  ): string {
    const firstName = user.first_name ?? user.firstName ?? '';
    const lastName = user.last_name ?? user.lastName ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fullName) {
      return fullName;
    }
    if (user.email) {
      return user.email;
    }
    return fallback;
  }

  private resolveUserIdentifier(candidate: any): string {
    if (!candidate || typeof candidate !== 'object') {
      return '';
    }
    const id = candidate.crm_id;
    return id ? String(id) : '';
  }

  private resolveCompanyLabelFromUser(user: any): string {
    if (!user || typeof user !== 'object') {
      return '';
    }
    const candidates = [
      user.company?.name,
      user.company_name,
      user.companyName,
      user.company?.label,
      user.companyInfo?.name,
    ];
    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  }

  private extractCompanyLabelFromUsers(rows: any[]): string | null {
    if (!Array.isArray(rows)) {
      return null;
    }
    for (const row of rows) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const candidate =
        row.company?.name ??
        row.company_name ??
        row.companyName ??
        row.company?.label ??
        row.company_label;
      if (candidate && typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  }

  private extractUserArray(response: any): any[] {
    const candidates = [
      response,
      response?.data,
      response?.data?.data,
      response?.data?.users,
      response?.data?.records,
      response?.data?.items,
      response?.users,
      response?.records,
      response?.items,
      response?.results,
      response?.rows,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return this.findFirstMatchingArray(response, (item) => this.isUserLike(item));
  }

  private findFirstMatchingArray(
    payload: any,
    predicate: (item: any) => boolean,
  ): any[] {
    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const queue: any[] = [payload];
    const visited = new Set<any>();

    while (queue.length) {
      const current = queue.shift();
      if (!current || visited.has(current)) {
        continue;
      }
      visited.add(current);

      if (Array.isArray(current)) {
        if (current.some((item) => predicate(item))) {
          return current;
        }
        current
          .filter((item) => item && typeof item === 'object')
          .forEach((item) => queue.push(item));
        continue;
      }

      Object.values(current).forEach((value) => {
        if (Array.isArray(value) || (value && typeof value === 'object')) {
          queue.push(value);
        }
      });
    }

    return [];
  }

  private isUserLike(candidate: any): boolean {
    return (
      !!candidate &&
      typeof candidate === 'object' &&
      (candidate.user_id ||
        candidate.id ||
        candidate.email ||
        candidate.first_name ||
        candidate.firstName ||
        candidate.last_name ||
        candidate.lastName)
    );
  }

  onMessageKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.shiftKey) {
      // Shift+Enter: allow default (new line)
      return;
    }
    // Enter without Shift: send message
    event.preventDefault();
    this.sendMessage();
  }

  hideConversation(conversation: ConversationSummary, event?: Event): void {
    event?.stopPropagation();
    if (!conversation?.id) {
      return;
    }
    if (conversation.hasUnread) {
      this.markConversationRead(conversation);
    }
    this.hiddenConversationIds.add(conversation.id);
    this.persistHiddenConversations();
    this.applyConversationVisibility();

    if (this.selectedConversation?.id === conversation.id) {
      this.selectedConversation = null;
      this.messages = [];
      this.messagesCursor = null;
    }
  }

  unhideConversation(conversation: ConversationSummary, event?: Event): void {
    event?.stopPropagation();
    if (!conversation?.id) {
      return;
    }
    this.hiddenConversationIds.delete(conversation.id);
    this.persistHiddenConversations();
    this.applyConversationVisibility();
  }

  toggleHiddenDrawer(): void {
    this.hiddenDrawerOpen = !this.hiddenDrawerOpen;
  }

  private loadHiddenConversations(): void {
    const key = this.getHiddenConversationStorageKey();
    if (!key) {
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return;
      }
      const ids = JSON.parse(raw);
      if (Array.isArray(ids)) {
        this.hiddenConversationIds = new Set(
          ids
            .map((item) => String(item || '').trim())
            .filter((item) => !!item),
        );
      }
    } catch (_) {
      this.hiddenConversationIds = new Set<string>();
    }
  }

  private persistHiddenConversations(): void {
    const key = this.getHiddenConversationStorageKey();
    if (!key) {
      return;
    }
    localStorage.setItem(
      key,
      JSON.stringify(Array.from(this.hiddenConversationIds)),
    );
  }

  private getHiddenConversationStorageKey(): string {
    return this.currentUserId
      ? `chat_hidden_conversations_${this.currentUserId}`
      : '';
  }

  private applyConversationVisibility(): void {
    const visible = this.allConversations.filter(
      (conversation) => !this.hiddenConversationIds.has(conversation.id),
    );
    const hidden = this.allConversations.filter((conversation) =>
      this.hiddenConversationIds.has(conversation.id),
    );
    this.conversations = this.sortConversations(visible);
    this.hiddenConversations = this.sortConversations(hidden);
    this.markHiddenUnreadConversationsAsRead();
    if (!this.hiddenConversations.length) {
      this.hiddenDrawerOpen = false;
    }
  }

  private markHiddenUnreadConversationsAsRead(): void {
    if (!this.currentUserId || !this.hiddenConversations.length) {
      return;
    }

    this.hiddenConversations.forEach((conversation) => {
      if (!conversation?.id || !conversation.hasUnread) {
        return;
      }
      if (this.hiddenReadSyncInFlight.has(conversation.id)) {
        return;
      }

      const lastReadAt =
        conversation.lastMessage?.created_at ??
        conversation.updated_at ??
        new Date().toISOString();

      this.hiddenReadSyncInFlight.add(conversation.id);
      this.chatService
        .markConversationRead(conversation.id, {
          userId: this.currentUserId,
          lastReadAt,
        })
        .subscribe({
          next: (response) => {
            this.hiddenReadSyncInFlight.delete(conversation.id);
            this.patchConversation(conversation.id, {
              hasUnread: false,
              lastReadAt: response.lastReadAt,
            });
            this.chatService.requestUnreadRefresh();
          },
          error: () => {
            this.hiddenReadSyncInFlight.delete(conversation.id);
          },
        });
    });
  }

  private refreshConversationsSilently(): void {
    if (!this.currentUserId || this.conversationsLoading) {
      return;
    }

    this.chatService
      .listConversations(this.currentUserId, {
        limit: 20,
        cursor: null,
        search: this.currentSearchTerm || null,
      })
      .subscribe({
        next: (response) => {
          const merged = [...(this.allConversations || [])];
          const indexById = new Map<string, number>();
          merged.forEach((item, index) => indexById.set(item.id, index));

          (response.items || []).forEach((incoming) => {
            const existingIndex = indexById.get(incoming.id);
            if (existingIndex === undefined) {
              indexById.set(incoming.id, merged.length);
              merged.push(incoming);
              return;
            }
            merged[existingIndex] = incoming;
          });

          this.allConversations = this.sortConversations(merged);
          this.applyConversationVisibility();

          if (this.selectedConversation) {
            const updatedSelection = this.allConversations.find(
              (item) => item.id === this.selectedConversation?.id,
            );
            if (updatedSelection) {
              this.selectedConversation = updatedSelection;
              if (updatedSelection.hasUnread) {
                this.markConversationRead(updatedSelection);
              }
            }
          }
        },
      });
  }

  private refreshSelectedConversationMessages(): void {
    if (!this.selectedConversation || !this.currentUserId || this.messagesLoading || this.isSending) {
      return;
    }

    this.chatService
      .listMessages(this.selectedConversation.id, {
        userId: this.currentUserId,
        limit: 30,
        cursor: null,
      })
      .subscribe({
        next: (response) => {
          if (!response?.items?.length) {
            return;
          }

          const merged = [...this.messages];
          const seen = new Set(this.messages.map((item) => item.id));
          response.items.forEach((item) => {
            if (!seen.has(item.id)) {
              merged.push(item);
            }
          });
          merged.sort((a, b) => {
            const aTime = Date.parse(a.created_at || '');
            const bTime = Date.parse(b.created_at || '');
            if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
              return 0;
            }
            return aTime - bTime;
          });
          this.messages = merged;
        },
      });
  }
}

interface ParticipantOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  displayName: string;
  searchIndex: string;
}
